import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

export interface AdminUserListItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  username: string | null;
  avatarUrl: string | null;
  role: number;
  verified: boolean;
  premium: boolean;
  isBanned: boolean;
  location: {
    country: string;
    state: string;
    city: string;
    coordinates?: { latitude: number; longitude: number };
  } | null;
}

export interface AdminUserListResponse {
  items: AdminUserListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface ListUsersArgs {
  search?: string;
  page?: number;
  limit?: number;
}

export interface AdminUserDetail {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  username: string | null;
  verified: boolean;
  role: number;
  avatarUrl: string | null;
  cover: string | null;
  bio: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  language: string | null;
  location: {
    country: string;
    state: string;
    city: string;
    coordinates?: { latitude: number; longitude: number };
  } | null;
  activities: Array<{ id: string; name: string }> | null;
  premium: boolean;
  isBanned: boolean;
  stats: {
    eventsCreated: number;
    eventsVisited: number;
    generalRating: number;
  } | null;
  socials: Array<{ value: string; link: string }>;
  grantType: string;
  fcmToken: string | null;
  onboardingState: {
    tourVersion: number;
    tourCompleted: boolean;
    tourSkipped: boolean;
    tourCurrentStep: string | null;
    tourSkippedSteps: string[];
    tourStartedAt: string | null;
    tourCompletedAt: string | null;
  } | null;
}

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery,
  tagTypes: ['User', 'UserList'],
  endpoints: (builder) => ({
    listUsers: builder.query<AdminUserListResponse, ListUsersArgs>({
      query: ({ search, page = 1, limit = 20 }) => {
        const params = new URLSearchParams();
        if (search && search.trim()) params.set('search', search.trim());
        params.set('page', String(page));
        params.set('limit', String(limit));
        return { url: `/admin/users?${params.toString()}` };
      },
      providesTags: ['UserList'],
    }),
    getUser: builder.query<AdminUserDetail, string>({
      query: (id) => ({ url: `/admin/users/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'User', id }],
    }),
    updateUserRole: builder.mutation<
      AdminUserDetail,
      { id: string; role: number }
    >({
      query: ({ id, role }) => ({
        url: `/admin/users/${id}/role`,
        method: 'PATCH',
        body: { role },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'User', id },
        'UserList',
      ],
    }),
    setUserBan: builder.mutation<
      AdminUserDetail,
      { id: string; isBanned: boolean }
    >({
      query: ({ id, isBanned }) => ({
        url: `/admin/users/${id}/ban`,
        method: 'PATCH',
        body: { isBanned },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'User', id },
        'UserList',
      ],
    }),
    deleteUser: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/admin/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserList'],
    }),
  }),
});

export const {
  useListUsersQuery,
  useGetUserQuery,
  useUpdateUserRoleMutation,
  useSetUserBanMutation,
  useDeleteUserMutation,
} = usersApi;
